package fr.manyverse.app.presentation.viewmodel

import androidx.lifecycle.viewModelScope
import fr.manyverse.app.domain.model.Character
import fr.manyverse.app.domain.model.CharacterToCreate
import fr.manyverse.app.domain.model.ImageData
import fr.manyverse.app.domain.model.Universe
import fr.manyverse.app.domain.usecase.CheckCharacterInActiveTableUseCase
import fr.manyverse.app.domain.usecase.CountCharactersUseCase
import fr.manyverse.app.domain.usecase.CreateCharacterUseCase
import fr.manyverse.app.domain.usecase.DeleteCharacterImageUseCase
import fr.manyverse.app.domain.usecase.DeleteCharacterUseCase
import fr.manyverse.app.domain.usecase.GetAllCharactersUseCase
import fr.manyverse.app.domain.usecase.GetAllUserActiveTablesUseCase
import fr.manyverse.app.domain.usecase.GetCharactersUseCase
import fr.manyverse.app.domain.usecase.GetUniversesUseCase
import fr.manyverse.app.domain.usecase.LeaveTableUseCase
import fr.manyverse.app.domain.usecase.UpdateCharacterUseCase
import fr.manyverse.app.domain.usecase.UploadCharacterImageUseCase
import fr.manyverse.app.presentation.error.UserErrorNotifier
import fr.manyverse.app.presentation.error.toUserMessage
import fr.manyverse.app.presentation.state.AsyncUiState
import kotlinx.coroutines.flow.MutableStateFlow
import kotlinx.coroutines.flow.StateFlow
import kotlinx.coroutines.flow.asStateFlow
import kotlinx.coroutines.launch

data class PendingCharacterDelete(
    val character: Character,
    val tableId: String,
    val tableName: String
)

class CharacterViewModel(
    private val getCharactersUseCase: GetCharactersUseCase,
    private val getAllCharactersUseCase: GetAllCharactersUseCase,
    private val countCharactersUseCase: CountCharactersUseCase,
    private val createCharacterUseCase: CreateCharacterUseCase,
    private val updateCharacterUseCase: UpdateCharacterUseCase,
    private val deleteCharacterUseCase: DeleteCharacterUseCase,
    private val uploadCharacterImageUseCase: UploadCharacterImageUseCase,
    private val deleteCharacterImageUseCase: DeleteCharacterImageUseCase,
    private val checkCharacterInActiveTableUseCase: CheckCharacterInActiveTableUseCase,
    private val leaveTableUseCase: LeaveTableUseCase,
    private val getUniversesUseCase: GetUniversesUseCase,
    private val getAllUserActiveTablesUseCase: GetAllUserActiveTablesUseCase,
    private val errorNotifier: UserErrorNotifier
) : BaseViewModel() {

    private var currentUniverseId: String? = null
    private var currentUserId: String? = null

    private val _characterListState =
        MutableStateFlow<AsyncUiState<List<Character>>>(AsyncUiState.Loading)
    val characterListState: StateFlow<AsyncUiState<List<Character>>> =
        _characterListState.asStateFlow()

    private val _createCharacterState =
        MutableStateFlow<AsyncUiState<Unit>>(AsyncUiState.Idle)
    val createCharacterState: StateFlow<AsyncUiState<Unit>> =
        _createCharacterState.asStateFlow()

    private val _updateCharacterState =
        MutableStateFlow<AsyncUiState<Unit>>(AsyncUiState.Idle)
    val updateCharacterState: StateFlow<AsyncUiState<Unit>> =
        _updateCharacterState.asStateFlow()

    private val _deleteCharacterState =
        MutableStateFlow<AsyncUiState<Unit>>(AsyncUiState.Idle)
    val deleteCharacterState: StateFlow<AsyncUiState<Unit>> =
        _deleteCharacterState.asStateFlow()

    private val _pendingDelete = MutableStateFlow<PendingCharacterDelete?>(null)
    val pendingDelete: StateFlow<PendingCharacterDelete?> = _pendingDelete.asStateFlow()

    private val _characterCountState = MutableStateFlow<AsyncUiState<Int>>(AsyncUiState.Idle)
    val characterCountState: StateFlow<AsyncUiState<Int>> = _characterCountState.asStateFlow()

    private val _universesById = MutableStateFlow<Map<String, Universe>>(emptyMap())
    val universesById: StateFlow<Map<String, Universe>> = _universesById.asStateFlow()

    private val loadCharactersAsync = cancellableAsync<List<Character>>()

    fun loadCharacters(universeId: String, userId: String, forceRefresh: Boolean = false) {
        currentUniverseId = universeId
        currentUserId = userId

        loadUniverses(userId)

        if (forceRefresh) {
            loadCharactersAsync(_characterListState) {
                getCharactersUseCase(universeId, userId)
            }
        } else {
            launchAsyncSilent(_characterListState) {
                getCharactersUseCase(universeId, userId)
            }
        }
    }

    fun loadAllCharacters(userId: String) {
        currentUniverseId = null
        currentUserId = userId

        loadUniverses(userId)

        launchAsyncSilent(_characterListState) {
            getAllCharactersUseCase(userId)
        }
    }

    fun loadCharacterCount(userId: String) {
        launchAsyncSilent(_characterCountState) {
            countCharactersUseCase(userId)
        }
    }

    fun createCharacter(
        universeId: String,
        userId: String,
        name: String,
        story: String,
        imageData: ImageData? = null
    ) {
        val character = CharacterToCreate(
            userId = userId,
            name = name,
            story = story
        )

        launchAsyncWithCallback(_createCharacterState, {
            val createResult = createCharacterUseCase(universeId, character)
            if (createResult.isFailure) {
                return@launchAsyncWithCallback createResult.map { }
            }

            val createdCharacter = createResult.getOrThrow()

            // Upload image if provided
            if (imageData != null) {
                val uploadResult =
                    uploadCharacterImageUseCase(userId, createdCharacter.id, imageData)
                if (uploadResult.isSuccess) {
                    val imagePath = uploadResult.getOrThrow()
                    // Update character with imagePath and WAIT for result
                    updateCharacterUseCase(
                        createdCharacter.id,
                        name,
                        story,
                        imagePath
                    )
                }
            }

            Result.success(Unit)
        }) {
            reloadCharacters()
        }
    }

    fun updateCharacter(
        userId: String,
        characterId: String,
        characterUniverseId: String,
        name: String,
        story: String,
        imageData: ImageData? = null,
        removeImage: Boolean = false,
        currentImageUrl: String? = null
    ) {
        launchAsyncWithCallback(_updateCharacterState, {
            var finalImageUrl: String? = currentImageUrl

            // Handle image removal
            if (removeImage && currentImageUrl != null) {
                deleteCharacterImageUseCase(userId, characterId)
                finalImageUrl = null
            }

            // Handle new image upload
            if (imageData != null) {
                val uploadResult = uploadCharacterImageUseCase(userId, characterId, imageData)
                if (uploadResult.isSuccess) {
                    finalImageUrl = uploadResult.getOrThrow()
                }
            }

            updateCharacterUseCase(
                characterId,
                name,
                story,
                finalImageUrl
            ).map { }
        }) {
            if (currentUniverseId != null) {
                loadCharacters(characterUniverseId, userId)
            } else {
                loadAllCharacters(userId)
            }
        }
    }

    fun requestDeleteCharacter(character: Character) {
        viewModelScope.launch {
            _deleteCharacterState.value = AsyncUiState.Loading
            checkCharacterInActiveTableUseCase(character.id, currentUserId ?: "")
                .onSuccess { table ->
                    _deleteCharacterState.value = AsyncUiState.Idle
                    if (table != null) {
                        _pendingDelete.value =
                            PendingCharacterDelete(character, table.id, table.name)
                    } else {
                        deleteCharacter(character.id)
                    }
                }
                .onFailure { error ->
                    _deleteCharacterState.value = AsyncUiState.Error(error.toUserMessage())
                    errorNotifier.notify(error)
                }
        }
    }

    fun confirmDeleteCharacter() {
        val pending = _pendingDelete.value ?: return
        _pendingDelete.value = null
        launchAsyncWithCallback(
            _deleteCharacterState,
            {
                val leaveResult = leaveTableUseCase(pending.tableId, currentUserId ?: "")
                if (leaveResult.isFailure) return@launchAsyncWithCallback leaveResult
                deleteCharacterUseCase(pending.character.id)
            },
            // La carte n'affiche pas d'erreur : l'échec passe par le snackbar
            onFailure = errorNotifier::notify
        ) {
            reloadCharacters()
        }
    }

    fun cancelDeleteCharacter() {
        _pendingDelete.value = null
    }

    fun deleteCharacter(characterId: String) {
        launchAsyncWithCallback(
            _deleteCharacterState,
            { deleteCharacterUseCase(characterId) },
            onFailure = errorNotifier::notify
        ) {
            reloadCharacters()
        }
    }

    private fun loadUniverses(userId: String) {
        viewModelScope.launch {
            // Built-in + universes the user owns (readable directly).
            val byId = getUniversesUseCase(userId)
                .getOrElse { emptyList() }
                .associateBy { it.id }
                .toMutableMap()

            // Universes owned by another GM: not readable via the universes table (RLS),
            // but their real name is in the joined table's config snapshot.
            getAllUserActiveTablesUseCase(userId).onSuccess { tables ->
                tables.forEach { (table, _) ->
                    val name = table.universeConfig.displayName
                    val isResolvable = table.universeId !in byId
                    if (isResolvable && name.isNotBlank()) {
                        byId[table.universeId] = Universe(
                            id = table.universeId,
                            displayName = name,
                            config = table.universeConfig
                        )
                    }
                }
            }

            if (byId.isNotEmpty()) {
                _universesById.value = byId
            }
        }
    }

    private fun reloadCharacters() {
        val userId = currentUserId ?: return
        val universeId = currentUniverseId
        if (universeId != null) {
            loadCharacters(universeId, userId)
        } else {
            loadAllCharacters(userId)
        }
    }

    fun resetCreateCharacterState() = resetState(_createCharacterState)
    fun resetUpdateCharacterState() = resetState(_updateCharacterState)
}
